<template>
  <div class="wrapper">
    <transition name="notify">
      <div v-if="showNotification" class="notification">
        Copied to clipboard
      </div>
    </transition>
    <div class="card">
      <h1 class="title">Prompt Templates</h1>
      <select v-model="selectedTemplate" class="select">
        <option disabled value="">Choose your template</option>
        <option v-for="(template, index) in templates" :key="index" :value="template">
          {{ template.name }}
        </option>
      </select>

      <transition name="fade">
        <div v-if="selectedTemplate" class="form">
          <div v-for="(input, index) in selectedTemplate.inputs" :key="index" class="input-group">
            <label>{{ input.label }}</label>
            <input
              v-if="input.type === 'text'"
              v-model="filled[input.key]"
              class="input"
              :placeholder="input.label"
            />
            <select
              v-else-if="input.type === 'dropdown'"
              v-model="filled[input.key]"
              class="select"
            >
              <option disabled value="">-- Choose --</option>
              <option v-for="option in input.options" :key="option">{{ option }}</option>
            </select>
          </div>

          <button class="generate-button" @click="copyResult">
            ✨ Generate & Copy
          </button>

          <transition name="fade">
            <div v-if="result" class="result">
              {{ result }}
            </div>
          </transition>
        </div>
      </transition>
    </div>
  </div>
</template>

<script>
export default {
  name: 'PromptTool',
  data() {
    return {
      selectedTemplate: null,
      filled: {},
      showNotification: false,
      templates: [
        {
          name: 'Reply formally and positively',
          text: 'Reply to the following [type] in a straight to the point, yet formal and positive tone. Use the exact language as the message. Message: [message]. Response goal: [goal]',
          inputs: [
            { key: 'type', label: 'Type (email or message)', type: 'dropdown', options: ['email', 'message'] },
            { key: 'message', label: 'Received message', type: 'text' },
            { key: 'goal', label: 'What do you want to say?', type: 'text' }
          ]
        },
        {
          name: 'Write message to person',
          text: 'Write a [type] to [recipient] in a straight to the point, yet formal and positive tone. Use the exact language as the message. Use this messy input to derive the message: [goal]',
          inputs: [
            { key: 'type', label: 'Type (email or message)', type: 'dropdown', options: ['email', 'message'] },
            { key: 'recipient', label: 'Recipient', type: 'text' },
            { key: 'goal', label: 'What should the message be about?', type: 'text' }
          ]
        }
      ]
    }
  },
  computed: {
    result() {
      if (!this.selectedTemplate) return ''
      let text = this.selectedTemplate.text
      this.selectedTemplate.inputs.forEach(input => {
        const value = this.filled[input.key] || ''
        text = text.replace(new RegExp(`\\[${input.key}\\]`, 'g'), value)
      })
      return text
    }
  },
  watch: {
    selectedTemplate(newTemplate) {
      this.filled = {}
      if (newTemplate) {
        newTemplate.inputs.forEach(input => {
          this.$set(this.filled, input.key, '')
        })
      }
    }
  },
  methods: {
    async copyResult() {
      const text = this.result
      if (!text) return
      if (navigator.clipboard && navigator.clipboard.writeText) {
        try {
          await navigator.clipboard.writeText(text)
        } catch (e) {
          this.fallbackCopy(text)
        }
      } else {
        this.fallbackCopy(text)
      }
      this.showNotification = true
      setTimeout(() => { this.showNotification = false }, 2000)

      setTimeout(() => {
        window.open('https://chat.openai.com/', '_self');
      }, 100);
    },
    fallbackCopy(text) {
      const textarea = document.createElement('textarea')
      textarea.value = text
      textarea.setAttribute('readonly', '')
      textarea.style.position = 'absolute'
      textarea.style.left = '-9999px'
      document.body.appendChild(textarea)
      textarea.select()
      document.execCommand('copy')
      document.body.removeChild(textarea)
    }
  }
}
</script>

<style scoped>
.wrapper {
  width: 100vw;
  min-height: 100vh;
  background: #f0f0f5;
  display: flex;
  justify-content: center;
  align-items: center;
  padding: 1rem;
  position: relative;
}

.notification {
  position: fixed;
  top: 1rem;
  left: 50%;
  transform: translateX(-50%);
  background: rgba(255, 255, 255, 0.6);
  backdrop-filter: blur(15px);
  padding: 0.75rem 1.5rem;
  border-radius: 12px;
  box-shadow: 0 4px 16px rgba(0, 0, 0, 0.1);
  color: #333;
  font-size: 0.95rem;
  z-index: 1000;
}

.notify-enter-active,
.notify-leave-active {
  transition: opacity 0.3s ease, transform 0.3s ease;
}

.notify-enter,
.notify-leave-to {
  opacity: 0;
  transform: translate(-50%, -10px);
}

.card {
  background: rgba(255, 255, 255, 0.7);
  backdrop-filter: blur(20px);
  border-radius: 24px;
  padding: 2rem;
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.1);
  width: 100%;
  max-width: 540px;
  display: flex;
  flex-direction: column;
}

.title {
  text-align: center;
  color: #333;
  font-size: 1.75rem;
  margin-bottom: 1rem;
}

.select,
.input {
  width: 100%;
  padding: 0.75rem 1rem;
  margin: 0.5rem 0 1.25rem;
  border-radius: 12px;
  background: rgba(255, 255, 255, 0.8);
  border: 1px solid #ccc;
  font-size: 1rem;
  color: #333;
  transition: border-color 0.3s ease;
  box-sizing: border-box;
}

.select:focus,
.input:focus {
  border-color: #888;
  outline: none;
}

.input-group label {
  color: #555;
  font-size: 0.95rem;
  margin-bottom: 0.25rem;
  display: block;
}

.generate-button {
  width: 100%;
  padding: 0.9rem;
  background: linear-gradient(90deg, #6c63ff 0%, #9a8cff 100%);
  border: none;
  color: white;
  font-size: 1rem;
  border-radius: 12px;
  margin-top: 1rem;
  cursor: pointer;
  transition: transform 0.2s ease;
}

.generate-button:hover {
  transform: scale(1.02);
}

.result {
  margin-top: 1.25rem;
  padding: 0.9rem;
  background: rgba(255, 255, 255, 0.8);
  border: 1px solid #ccc;
  border-radius: 12px;
  color: #333;
  font-size: 0.95rem;
  word-break: break-word;
}

.fade-enter-active,
.fade-leave-active {
  transition: opacity 0.4s ease;
}

.fade-enter,
.fade-leave-to {
  opacity: 0;
}

@media (max-width: 768px) {
  .card {
    padding: 1.5rem;
    max-width: 100%;
  }

  .title {
    font-size: 1.5rem;
    margin-bottom: 0.75rem;
  }

  .select,
  .input {
    padding: 0.65rem 0.9rem;
    font-size: 0.95rem;
    margin: 0.4rem 0 1rem;
  }

  .generate-button {
    padding: 0.8rem;
    font-size: 0.95rem;
  }

  .result {
    padding: 0.8rem;
    font-size: 0.9rem;
  }
}

@media (max-width: 480px) {
  .wrapper {
    padding: 0.5rem;
  }

  .card {
    padding: 1rem;
    border-radius: 20px;
  }

  .title {
    font-size: 1.25rem;
  }

  .select,
  .input {
    padding: 0.5rem 0.75rem;
    font-size: 0.9rem;
  }

  .generate-button {
    padding: 0.7rem;
    font-size: 0.9rem;
  }

  .result {
    padding: 0.7rem;
    font-size: 0.9rem;
  }
}
</style>



// WEBPACK FOOTER //
// src/components/PromptTool.vue